import React, { useState, useContext, useMemo, useEffect } from 'react';
import { PLIConfigStep, PLIConfigTemplateSection, PLISectionEditMode } from 'src/models/calculation-builder/pli-config/PLIConfigTemplate';
import { Box, Button, ButtonDropdown, ButtonDropdownProps, Icon, Input, Popover, Toggle } from '@amzn/awsui-components-react';
import ATPTextAreaAutoComplete, { AUTOSUGGEST_TRIGGER } from 'src/components/shared/ATPTextAreaAutoComplete';
import CONSTANTS from 'src/utils/constants';
import { CalculationBuilderContext } from '../CalculationBuilderView';
import { renderExpressionParts } from 'src/components/shared/FormulaExpressionFunctionComponents';
import { TPEReactTable } from 'src/components/shared/TPEReactTable';
import DomUtils from 'src/utils/DomUtils';
import { SavingStatus } from 'src/models/common/SavingStatus';
import { PLIConfigContext } from './PLIConfigContainer';
import { ACTIONS } from 'src/services/calculation-builder/PLIConfigReducer';
import StringUtils from 'src/utils/stringUtils';
import { Row } from 'react-table';
import TPEAction from 'src/models/common/TPEAction';
import ServiceCollection from 'src/services/ServiceCollection';

// Cell Editors
const EditableCell = ({
    value: initialValue,
    row: { index, original },
    column: { id },
    onRowUpdated, // This is a custom function that we supplied to our table instance
    editorsData, // Editors data contains data needed to render the editors
}: { value: any, initialValue: any, row: any, column: any, onRowUpdated: any, editorsData: any}) => {
    const [value, setValue] = React.useState(initialValue);
    
    
    // If the initialValue is changed external, sync it up with our state
    React.useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    const onTextAreaBlur = (args: any) => {
        if (StringUtils.isNullOrEmpty(args.textAreaValue) || args.textAreaValue.indexOf(AUTOSUGGEST_TRIGGER) >= 0){
            return;
        }
        onRowUpdated(index, id, args.textAreaValue);
    }

    const onBlur = () => {
        onRowUpdated(index, id, value)
    }

    const onChange = (e: any) => {
        setValue(e.detail.value);
    }

    const step = original as PLIConfigStep;
    
        
    if (step.isBeingEdited){
        switch (id) {
            case CONSTANTS.PLI_CONFIG_STEP_FIELDS.OVERVIEW: {
                const newSuggestionsList = editorsData.stepsList.filter((x: { sequence: number; }) => x.sequence < step.sequence)
                    .map(
                        (x:PLIConfigStep) => ({name: `S${x.sequence}.${x.description}`, value: x.description, type: 'Previous Steps', id: x.stepId})
                    ).concat(editorsData.suggestionsList);
                if (step.isMarkupRate){
                    return <div key={`${id}_${index}`} className={`save-status-${original.savingStatus} cell-text ${id}Cell`}>
                                <Input key={`${id}_${index}`} className={"dataSourcesButton " + id + "Editor" + original.allocationGroupId} placeholder="Enter value" value={value} onChange={onChange} onBlur={onBlur} />
                                {original.errorIndicator && <span className="inlineErrorContainer">{original.errorIndicator}</span>}
                            </div>;
                }
                return <ATPTextAreaAutoComplete
                            key={`${id}_${index}`}
                            editorsData={{suggestionsList: newSuggestionsList, onTextAreaCaretPositionChanged: editorsData.onTextAreaCaretPositionChanged}} 
                            initialValue={value}
                            onTextAreaBlur={onTextAreaBlur} />
            }
            case CONSTANTS.PLI_CONFIG_STEP_FIELDS.DESCRIPTION:
                if (step.isMarkupRate){
                    return getStepNameReadOnlyCell(id, index, value, original);
                }
                return <div key={`${id}_${index}`} className={`save-status-${original.savingStatus} cell-text ${id}Cell`}>
                    <Input key={`${id}_${index}`} className={"dataSourcesButton " + id + "Editor" + original.allocationGroupId} placeholder="Enter value" value={value} onChange={onChange} onBlur={onBlur} />
                    {original.errorIndicator && <span className="inlineErrorContainer">{original.errorIndicator}</span>}
                </div>;
        }
    }
    else {
        switch (id) {
            case CONSTANTS.PLI_CONFIG_STEP_FIELDS.DESCRIPTION:
                return getStepNameReadOnlyCell(id, index, value, original);
            case CONSTANTS.PLI_CONFIG_STEP_FIELDS.OVERVIEW: {
                const isOverviewSectionStep = editorsData.isOverviewSection && editorsData.sectionNames.find((x:string) => step.description == x);
                return <div key={`${id}_${index}`} className={`save-status-${step.savingStatus}`}>
                        {isOverviewSectionStep? "-" : (StringUtils.isNullOrEmpty(step.expression)? "Click to enter PLI step formula" : step.expressionFormatted)}
                        {editorsData.shouldShowValues && step.valueBreakdown? <div className="greyBadge">{step.valueBreakdown}</div> : null }
                    </div>;
            }
        }
    }
    return <div key={`${id}_${index}`} className={`save-status-${step.savingStatus} cell-text ${id}Cell`}>{value}</div>;
};

// Set our editable cell renderer as the default Cell renderer
const defaultCellEditor = {
    Cell: EditableCell,
}

const contextMenuActions = {
    EDIT: { text: "Edit", id: "Edit" },
    DUPLICATE: { text: "Duplicate", id: "Duplicate" },
    ADD_COMMENT: { text: "Add comment", id: "Add comment" },
    REMOVE: { text: "Remove", id: "Remove" },
    ADD_STEP_BEFORE: { text: "Add step before", id: "Add step before" },
    ADD_STEP_AFTER: { text: "Add step after", id: "Add step after" }
}

const stepsMenuItems = [
    contextMenuActions.EDIT,
    contextMenuActions.DUPLICATE,
    contextMenuActions.REMOVE,
    /* Hidding comments feature since API will be released on P1 */
    //contextMenuActions.ADD_COMMENT,
    contextMenuActions.ADD_STEP_BEFORE,
    contextMenuActions.ADD_STEP_AFTER
];

const nonEditableStepsMenuItems: readonly ButtonDropdownProps.ItemOrGroup[] = [
    
];

export default function PLITemplateSectionGrid(props: {section: PLIConfigTemplateSection}) {
    const { calcBuilderState, calcBuilderDispatch, services } = useContext(CalculationBuilderContext);
    const { state, dispatch } = useContext(PLIConfigContext);
    const { dataSourceRecords, steps: calculationSteps } = calcBuilderState;
    const { stepBeingEdited, templateSections } = state;
    const { section } = props;
    const { steps } = section;
    
    // Columns without a Cell property have rendering handled by the EditableCell renderer
    const columnDefinitions = useMemo(() => [
        {
            accessor: CONSTANTS.PLI_CONFIG_STEP_FIELDS.SEQUENCE,
            Header: (e: any) => <div className="cell-text-non-sortable">#</div>,
        },
        {
            accessor: CONSTANTS.PLI_CONFIG_STEP_FIELDS.DESCRIPTION,
            Header: (e: any) => <div className="cell-text-non-sortable">Step name</div>,
        },
        {
            accessor: CONSTANTS.PLI_CONFIG_STEP_FIELDS.OVERVIEW,
            Header: <div className="headerToggle">Calculation overview &nbsp;
                        <Popover
                            data-class="basicPopover"
                            className="calcOverviewPopover"
                            dismissButton={false}
                            position="right"
                            size="small"
                            triggerType="custom"
                            content="Click the expression to edit. Type '@' to display the autocomplete menu."
                        >
                            <Icon
                            name="status-info"
                            size="small"
                            variant="link"
                            />
                        </Popover>
                        <div className="toggleDiv">
                            <Toggle className="showDecimalsToggle"
                                    onChange={() => dispatch(ACTIONS.TOGGLE_SHOW_VALUES.withPayload(section))}
                                    checked={section.showValues}
                                    ><div className="cell-text-toggle">Show values</div>
                            </Toggle>
                        </div>
                    </div>
        },
        {
            accessor: `${CONSTANTS.PLI_CONFIG_STEP_FIELDS.OUTPUT}Formatted`,
            Header: (e: any) => <div className="cell-text-non-sortable">Formula output</div>,
        },
        {
            accessor: "actions",
            Header: (e: any) => <div className="cell-text-non-sortable">Actions</div>,
            Cell: (e: any) => {
                return (
                <div className="rowActions formulaOutputCell">
                    <span>&nbsp;</span>
                    <span>&nbsp;</span>
                    {e.row.original.hasComments ?
                        <Button iconName="contact" variant="icon"
                                onClick={() => dispatch(ACTIONS.SET_NEW_COMMENT_MODAL_PAYLOAD.withPayload({step: e.row.original}))}/> :
                        <span>&nbsp;</span>
                    }
                    <Button iconName="status-info" variant="icon"
                            onClick={() => {}}/>
                    {!section.isOverview &&
                        <ButtonDropdown 
                            className="rowContextMenu calcStepsContextMenu" 
                            onItemClick={(bde) => {
                                onStepMenuClicked(bde.detail, e.row, section, dispatch, services, e.row.original.stepId);
                            }}
                            items={e.row.original.isEditable ? stepsMenuItems : nonEditableStepsMenuItems}
                        >
                        </ButtonDropdown>
                    }
                </div>
                )
            }
        }
    ], [steps, stepBeingEdited?.expression, section.showValues]);

    
    const [skipPageReset, setSkipPageReset] = React.useState(false);
    const [formattedRecords, setFormattedRecords] = useState([] as any[]);
    const [gridInitialState, setGridInitialState] = useState({} as any);

    React.useEffect(() => {
        if (steps == null) {
            return;
        }
        const dataSourceDescriptions = dataSourceRecords.map(x => ({name: x.description}));
        setFormattedRecords(steps.map(x => {
            const parts = services.readOnlyCalculationService.breakExpressionIntoParts(x.expression, calculationSteps.map(x => ({name:x.stepName, visibleSequence: x.visibleSequence})), dataSourceDescriptions, new Set(), steps);
            x.expressionTokens = parts;
            const errorsList = (x.validationErrors || []).map(x => <li>{x.message}</li>);
            const errorsContent = errorsList.length > 0? <React.Fragment><b>Validation Errors</b><ul>{errorsList}</ul></React.Fragment> : null;
            return {
                ...x,
                isEditable: true,
                isBeingEdited: x.isBeingEdited,
                savingStatus: SavingStatus.Unsaved,
                expression: x.expression || '',
                expressionFormatted: renderExpressionParts(parts),
                formulaOutputFormatted: isNaN(x.formulaOutput as any)? x.formulaOutput : services.formattingService.formatString(x.formulaOutput || '', true, 10),
                errorIndicator: errorsContent? <Popover key={`popFormulaError_${x.sequence}`}
                                    className="errorMessagePopover"
                                    dismissButton={false}
                                    position="top"
                                    size="small"
                                    triggerType="custom"
                                    content={
                                        errorsContent
                                    }
                                >
                                    <Icon
                                    name="status-warning"
                                    size="normal"
                                    variant="warning"
                                    />
                                </Popover> : null
            };
        }));
    }, [calculationSteps, dataSourceRecords, steps, stepBeingEdited?.expression]);

    // When our cell renderer calls updateMyData, we'll use
    // the rowIndex, columnId and new value to update the
    // original data
    const onRecordUpdated = (rowIndex: number, columnId: any, value: any) => {
        // We also turn on the flag to not reset the page
        setSkipPageReset(true)
        if (columnId === CONSTANTS.PLI_CONFIG_STEP_FIELDS.OVERVIEW) {
            dispatch(ACTIONS.UPDATE_CURRENT_STEP.withPayload({
                field: columnId,
                value: value,
                extraValue: services.readOnlyCalculationService.breakExpressionIntoParts(
                    value,
                    calculationSteps.map(x => ({name: x.stepName, visibleSequence: x.visibleSequence})),
                    dataSourceRecords.map(x => ({name: x.description})),
                    new Set(),
                    section.steps,
                ),
                from:"onRecordUpdated overview"
            }));
        }
        else {
            dispatch(ACTIONS.UPDATE_CURRENT_STEP.withPayload({field: columnId, value: value,
                from:"onRecordUpdated no-overview"}));
        }
    }


    const onFormulaEditorCaretPositionChanged = (position: number) => {
        dispatch(ACTIONS.SET_EDITOR_CARET_POSITION.withPayload(position));
    }

    const onRowClicked = (e: any, step: PLIConfigStep, index:number) => {
        if (section.editMode != PLISectionEditMode.EDITABLE && !step.isMarkupRate) {
            return;
        }
        // We don't do anything if user clicks on the formula output, error container tooltip or sequence cell
        if (
            DomUtils.isDescendantOfClassName(e.target, 'formulaOutputCell') ||
            DomUtils.isDescendantOfClassName(e.target, 'calStepsSequenceCell') ||
            DomUtils.isDescendantOfClassName(e.target, 'stepErrorContainer')
        ){
            return;
        }
        if (step.savingStatus === SavingStatus.Unsaved) {
            dispatch(ACTIONS.SET_STEP_BEING_EDITED.withPayload({step, section}));
        }
    }
    
    return <TPEReactTable {...{
        data: formattedRecords,
        columnDefinitions,
        className: "calculationSteps pliSteps",
        sortable: false,
        onRowClicked: onRowClicked,
        initialState: gridInitialState,
        editableOptions: {
            defaultColumn: defaultCellEditor,
            onRowUpdated: onRecordUpdated,
            skipPageReset,
            editorsData: {
                shouldShowValues: section.showValues,
                suggestionsList: [...dataSourceRecords.map(x => ({name: x.description, type: "Data sources", value: x.description})),
                    ...calculationSteps.map(x => ({name: x.stepName, type: "Calculation steps", value: x.stepName}))
                ],
                stepsList: formattedRecords,
                onTextAreaCaretPositionChanged: onFormulaEditorCaretPositionChanged,
                isOverviewSection: section.isOverview,
                sectionNames: templateSections.map(x => x.name)
            }
        }
    }} />;
}

function onStepMenuClicked(detail: ButtonDropdownProps.ItemClickDetails, row: Row, section: PLIConfigTemplateSection, dispatch: React.Dispatch<TPEAction>, services: ServiceCollection, stepId: string): void {
    const step = row.original as PLIConfigStep;
    switch(detail.id) {
        case contextMenuActions.EDIT.id:
            dispatch(ACTIONS.SET_STEP_BEING_EDITED.withPayload({step,section}));
        break;
        case contextMenuActions.DUPLICATE.id:            
            dispatch(ACTIONS.DUPLICATE_SECTION_STEP.withPayload({ index: row.index + 1, originalStep: step, section }));
        break;
        case contextMenuActions.REMOVE.id:
            dispatch(ACTIONS.SET_REMOVE_STEP_PAYLOAD.withPayload({ index: row.index, step }))
        break;
        case contextMenuActions.ADD_STEP_BEFORE.id:
            dispatch(ACTIONS.ADD_SECTION_STEP_BEFORE.withPayload({ index: row.index, section, newStep: services.pliConfigService.createNewStep(section, step.sequence) }))
        break;
        case contextMenuActions.ADD_STEP_AFTER.id:
            dispatch(ACTIONS.ADD_SECTION_STEP_AFTER.withPayload({ index: row.index, section, newStep: services.pliConfigService.createNewStep(section, step.sequence) }))
        break;
        case contextMenuActions.ADD_COMMENT.id:
            dispatch(ACTIONS.SET_NEW_COMMENT_MODAL_PAYLOAD.withPayload({ step }))
        break;
    }
}

function getStepNameReadOnlyCell(id: any, index: any, value: any, original: any) {
    return <div key={`${id}_${index}`} className={`save-status-${original.savingStatus} cell-text formula_${id}Cell`}>
                <span>{StringUtils.isNullOrEmpty(value)? "Enter step name" : value}</span>
                {StringUtils.isNullOrEmpty(original.descriptionHelp)? null : <><br /><Box color="text-status-inactive" fontSize="body-s">{original.descriptionHelp}</Box></>}
                {original.errorIndicator && <div className="stepErrorContainer">{original.errorIndicator}</div>}
            </div>;
}
